/**
 * Copyright © 2026 Adnuntius AS.
 */
import {Component, Inject, OnInit} from '@angular/core';
import {completeCopyModal} from '../common/util/complete-copy-modal';
import {Subject} from 'rxjs';
import {BsModalRef} from 'ngx-bootstrap/modal';
import {nextCopyTitle} from '../../components/util/next-copy-title';
import {Uuid} from '../../components/util/uuid';
import {FolioResource} from '../../components/api/resources/resource/folio.resource';
import {AdnModalLoadingService} from '../common/util/adn-modal-loading.service';

export interface FolioCopyResult {
  model: {id: string};
  destination: boolean;
}

@Component({
  standalone: false,
  selector: 'adn-folio-copy-modal',
  template: `
    <form class="form-horizontal" (ngSubmit)="copy()">
      <div class="modal-header dialogAddStuff">
        <button type="button" class="close" (click)="dismiss()">&times;</button>
        <h4 class="modal-title">
          <span>Copy Folio</span>
          <small>{{ originalName }}</small>
        </h4>
      </div>
      <div class="modal-body">
        <div class="form-group">
          <label class="col-md-3 control-label">{{ 'label.name' | ogTranslate }}</label>
          <div class="col-md-9">
            <input type="text" class="form-control" name="folio.name" [(ngModel)]="name" required>
          </div>
        </div>
        <div class="form-group">
          <label class="control-label col-md-3">Include in Copy</label>
          <div class="col-md-9">
            <div class="checkbox">
              <label>
                <input type="checkbox" [(ngModel)]="copyLineItems" name="copy.lineItems">
                <span>Line Items</span>
              </label>
            </div>
            <div class="checkbox">
              <label>
                <input type="checkbox" [(ngModel)]="copyCreatives" name="copy.creatives">
                <span>{{ 'creative.plural' | ogTranslate }}</span>
              </label>
            </div>
          </div>
        </div>
        <div class="form-group" *ngIf="copyLineItems">
          <label class="control-label col-md-3">Creative Set State</label>
          <div class="col-md-9">
            <select class="form-control" name="copy.userState" [(ngModel)]="userState">
              <option [ngValue]="''">Copy Includes Creative Set Status</option>
              <option [ngValue]="'INCOMPLETE'">{{ 'creativeSet.userState.INCOMPLETE' | ogTranslate }}</option>
              <option [ngValue]="'APPROVED'">{{ 'creativeSet.userState.APPROVED' | ogTranslate }}</option>
              <option [ngValue]="'PAUSED'">{{ 'creativeSet.userState.PAUSED' | ogTranslate }}</option>
            </select>
            <div class="small btnGroupExplanation">
              <span *ngIf="userState === ''">The creative set copy has the same status as the original creative set.</span>
              <span *ngIf="userState === 'INCOMPLETE'">Copied creative set is set to incomplete.</span>
              <span *ngIf="userState === 'APPROVED'">Copied creative set runs as per normal.</span>
              <span *ngIf="userState === 'PAUSED'">Copied cretive set is paused.</span>
            </div>
          </div>
        </div>
        <div class="checkbox text-right">
          <label class="checkbox">
            <input type="checkbox" [(ngModel)]="destination" name="copy.destination">
            <span class="small">Go to new folio after copy</span>
          </label>
        </div>
      </div>
      <div class="modal-footer">
        <a class="btn btn-link btn-xs" (click)="dismiss()">{{ 'button.cancel' | ogTranslate }}</a>
        <button type="submit" class="btn btn-default" [disabled]="disabled">{{ 'label.copy' | ogTranslate }}</button>
      </div>
    </form>
  `
})
export class FolioCopyModalComponent implements OnInit {
  model: any;

  readonly onCopied = new Subject<FolioCopyResult>();

  originalName = '';
  name = '';
  copyLineItems = true;
  copyCreatives = true;
  copyTargeting = true;
  userState = '';
  destination = true;
  disabled = false;

  constructor(
    public bsModalRef: BsModalRef,
    @Inject(FolioResource) private readonly Folio: any,
    @Inject(AdnModalLoadingService) private readonly adnModalLoading: any
  ) {
  }

  ngOnInit(): void {
    this.originalName = this.model?.name || '';
    this.name = nextCopyTitle(this.model?.name || '');
  }

  copy(): void {
    this.disabled = true;
    this.adnModalLoading.applyModalLoading();
    const newId = Uuid.generate();
    const folio = {
      ...this.model,
      name: this.name
    };
    const copyRequest = this.Folio.copyFolio(
      folio,
      newId,
      this.userState,
      this.copyLineItems,
      this.copyTargeting,
      this.copyCreatives
    );
    Promise.resolve(copyRequest.$promise || copyRequest).then(() => {
      completeCopyModal(this.bsModalRef, this.onCopied, {model: {id: newId}, destination: this.destination});
      this.adnModalLoading.removeModalLoading();
    }, () => {
      this.disabled = false;
      this.adnModalLoading.removeModalLoading();
    });
  }

  dismiss(): void {
    this.bsModalRef.hide();
  }
}
